import { usePaginateAdvancedActivityFeeds } from "../../../utils/api/functions/advancedActivityFeeds";
import { FeedContext } from "../../../utils/context/feedContext";
import LoadingPostItem from "../../loading-placeholders/LoadingPostItem";
import VideoDisplay from "../../videos/VideoDisplay";
import Share from "../Share";
import TanStackFeedList from "./VirtualizedList";
import { useFeedActions } from "./useFeedActions";
import useFeedModals from "./useFeedModals";
import React, { useEffect, useMemo, useState } from "react";
import EditPostModal from "../feed/EditPostModal";
import ReportPostModal from "../feed/ReportPostModal";
import BlockModal from "../feed/BlockModal";

const buildFeedSignature = (params = {}) => {
  const safeEntries = Object.entries(params ?? {}).sort(([a], [b]) => a.localeCompare(b));
  return JSON.stringify(safeEntries);
};

const dedupePostsByActionId = (posts = []) => {
  const seen = new Set();
  return posts.filter((post, idx) => {
    const actionId = post?.feed?.action_id ?? post?.action_id ?? post?.id;
    if (actionId === undefined || actionId === null) {
      return true;
    }
    const key = String(actionId);
    if (seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });
};

const FeedV4 = ({ feed_params = {}, CreatePostComponent = null, can_post = true }) => {
  if (!feed_params?.limit) feed_params.limit = 20;
  // Enable reactions by default? (1 = enabled, 0 = disabled)
  //if (!feed_params?.post_elements) feed_params.post_elements = 1;
  const {
    parsedPosts,
    data,
    isLoading,
    mutate,
    setSize,
    hasMore,
    isLoadingMore
  } = usePaginateAdvancedActivityFeeds(feed_params);

  const feedSignature = useMemo(() => buildFeedSignature(feed_params), [feed_params]);
  const [activeSignature, setActiveSignature] = useState(feedSignature);
  const [displayedPosts, setDisplayedPosts] = useState(() => dedupePostsByActionId(parsedPosts));
  const [isDisplayLoading, setIsDisplayLoading] = useState(isLoading);

  const {
    handleLike,
    handleUnlike,
    handleDelete,
    handleHidePost,
    handleUpdate,
    handleEditPost,
    handleReportPost,
    handleBlockByPostId
  } = useFeedActions({ data, mutate });

  const {
    shareData,
    showShare,
    hideShare,
    attachmentData,
    showAttachment,
    hideAttachment,
    showEditPost, 
    hideEditPost,
    editPostId,
    showReportPost,
    hideReportPost,
    reportPostId,
    showBlock,
    hideBlock, 
    blockByPostId
  } = useFeedModals();

  const postHandles = {
    handleLike,
    handleUnlike,
    handleShowShare: showShare,
  };

  // const createPostRef = useRef(
  //   can_post && CreatePostComponent ? (
  //     <CreatePostComponent
  //       callMutate={mutate}
  //       subject_type={feed_params.subject_type}
  //       subject_id={feed_params.subject_id}
  //     />
  //   ) : null
  // );

  // const createPostEl = createPostRef.current;

  const createPostEl =
  can_post && CreatePostComponent ? (
    <CreatePostComponent
      callMutate={mutate}
      subject_type={feed_params.subject_type}
      subject_id={feed_params.subject_id}
    />
  ) : null;

  useEffect(() => {
    if (activeSignature === feedSignature) {
      return;
    }

    setActiveSignature(feedSignature);
    setDisplayedPosts([]);
    setIsDisplayLoading(true);
    sessionStorage.removeItem("postFeedScrollTop");

    let cancelled = false;
    const resetFeed = async () => {
      try {
        await mutate([], false);
        await setSize(1);
        if (!cancelled) {
          await mutate(undefined, true);
        }
      } catch (error) {
        if (!cancelled) {
          console.error("Failed to reset feed data", error);
          setIsDisplayLoading(false);
        }
      }
    };

    resetFeed();

    return () => {
      cancelled = true;
    };
  }, [feedSignature, activeSignature, mutate, setSize]);

  useEffect(() => {
    if (activeSignature !== feedSignature) {
      return;
    }

    setDisplayedPosts(dedupePostsByActionId(parsedPosts));

    if (parsedPosts.length > 0) {
      setIsDisplayLoading(false);
      return;
    }

    if (!isLoading && !isLoadingMore) {
      setIsDisplayLoading(false);
    }
  }, [parsedPosts, activeSignature, feedSignature, isLoading, isLoadingMore]);

  const loadMoreItems = () => {
    setSize((prev) => prev + 1);
  };

  const showInitialLoader = isDisplayLoading && displayedPosts.length === 0;

  if (showInitialLoader) {
    return (
      <>
        {createPostEl}
        <LoadingPostItem />
      </>
    );
  }

  if (displayedPosts.length === 0 && !isDisplayLoading && !isLoadingMore) {
    return (
      <>
        {createPostEl}
        <div className="posts-feed col">
          <div style={{ textAlign: "center", paddingTop: "1.5rem" }}>
            No posts here yet...
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <FeedContext.Provider
        value={{
          handleUpdate, handleDelete, handleHidePost, handleShowAttachment: showAttachment,
          handleEditPost, showEditPost, posts: displayedPosts, showReportPost, hideReportPost, reportPostId,
          handleReportPost, handleBlockByPostId, hideBlock, showBlock, blockByPostId
        }}
      >
        <div className="posts-feed col">
            {/* TANSTACK */}
          <TanStackFeedList
            key={activeSignature}
            resetKey={activeSignature}
            posts={displayedPosts}
            handles={postHandles}
            loadMoreItems={loadMoreItems}
            hasMore={hasMore}
            CreatePostEl={createPostEl}
            isLoading={isLoadingMore}
            can_post={can_post}
          />
          <EditPostModal
            postId={editPostId}
            handleClose={hideEditPost}
            showModal={editPostId !== 0}
          />
          <ReportPostModal
            postId={reportPostId}
            handleClose={hideReportPost}
            showModal={reportPostId !== 0}
          />
          <BlockModal
            postId={blockByPostId}
            handleClose={hideBlock}
            showModal={blockByPostId !== 0}
          />
          <VideoDisplay show={!!attachmentData} onHide={hideAttachment} {...attachmentData} />
          <Share show={!!shareData} onHide={hideShare} data={shareData} />
        </div>
      </FeedContext.Provider>
    </>
  );
};

export default FeedV4;